import createDOMPurify from 'dompurify';
import { METADATA_TAG_NAMES } from './dom/content';
import { unwrapElement } from './dom/element';
import { getGlobalWindowOrThrow } from './dom/global';
import { isElementWithTagName } from './dom/node';
import { inlineStyles } from './inlineStyles';

type DOMPurify = ReturnType<typeof createDOMPurify>;

let domPurifyInstance: DOMPurify | undefined;

function getDomPurify(): DOMPurify {
  if (!domPurifyInstance) {
    const window = getGlobalWindowOrThrow();

    // Create our own instance, so we're not affected when loaded into an existing project.
    domPurifyInstance = createDOMPurify(window);

    // CAUTION: a bad DOMPurify hook can prevent subtrees from being sanitized.
    domPurifyInstance.addHook('afterSanitizeAttributes', node => {
      maybeRemoveGoogleSheetsAttributes(node);
    });
  }

  return domPurifyInstance;
}

export interface SanitizeHtmlOptions {
  addAllowedAttributes?: string[];
}

export function sanitizeHtml(dirtyHtml: string, options: SanitizeHtmlOptions = {}): string {
  // Purify the incoming HTML using DOMPurify.
  const cleanDom = getDomPurify().sanitize(dirtyHtml, {
    RETURN_DOM: true,
    // Move any <style> tags from the head into the body, so we can inline them.
    FORCE_BODY: true,
    // Remove all metadata tags except <style> tags.
    FORBID_TAGS: Array.from(METADATA_TAG_NAMES).filter(tagName => tagName !== 'STYLE'),
    // Allow additional attributes.
    ADD_ATTR: options.addAllowedAttributes,
  });

  // Post-process.
  unwrapGoogleDocsWrappers(cleanDom);

  // Stringify to HTML, and unpack TrustedHTML if necessary.
  const cleanHtml = String(cleanDom.innerHTML);

  return inlineStyles(cleanHtml);
}

function unwrapGoogleDocsWrappers(container: HTMLElement) {
  for (const element of container.querySelectorAll<HTMLElement>('b[id], span[id]')) {
    if (element.id.startsWith('docs-internal-guid-') && element.style.fontWeight === 'normal') {
      unwrapElement(element);
    }
  }
}

const GOOGLE_SHEETS_WRAPPER_ATTRIBUTES = ['data-sheets-value', 'data-sheets-userformat'];

function maybeRemoveGoogleSheetsAttributes(node: Node) {
  if (isElementWithTagName(node, 'span')) {
    GOOGLE_SHEETS_WRAPPER_ATTRIBUTES.forEach(name => node.removeAttribute(name));
  }
}
